// ============================================
// 필드 실행 설정 (키워드 매칭 없음)
// ============================================
// LLM이 availableFields를 보고 실제 필드명을 직접 전달.
// 이 파일은 코드 실행에 필요한 alias/compute/display 라벨만 관리.

import { addAge, addRemainingDays } from './aiComputeHandlers.js';

// ============================================
// 1. 필드 레지스트리 — field → { aliases, computes }
// ============================================

export const FIELD_REGISTRY = {
  // 회원 기본
  birthDay:       { aliases: ['birthday', 'birthDate'], normalize: true, computes: { age: { virtual: 'age', handler: addAge }, birthday: {} } },
  gender:         { aliases: [] },
  name:           { aliases: ['memberName', 'userName', 'teacherName', 'coachName'], normalize: true },
  phone:          { aliases: ['tel', 'mobile', 'phoneNumber', 'contactNumber'], normalize: true },
  profile:        { aliases: ['coachProfile'], normalize: true },

  // 식별자 — 멀티턴 cross-reference 매칭용 (다른 인텐션들 사이 회원 ID 키 통일)
  userId:         { aliases: ['linkCoachId'], normalize: true },

  // 회원권
  viewEndDate:    { aliases: ['endDate', 'expiryDate'], computes: { dday: { virtual: 'remainingDays', handler: addRemainingDays } }, keywords: ['만료', '만료예정', '잔여'], defaultSort: 'asc' },
  viewStartDate:  { aliases: ['startDate'] },
  viewMembershipName: { aliases: ['membershipName', 'memberShipName'] },
  statusName:     { aliases: ['state', 'status'] },

  // 금액
  totalPrice:     { aliases: ['price', 'maxPrice', 'totalMaxPrice'], keywords: ['매출', '결제', '금액'], defaultSort: 'desc' },
  price:          { aliases: ['totalPrice'] },
  point:          { aliases: [] },
  discountPrice:  { aliases: ['discountRate'] },

  // 수업/출석
  lessonName:     { aliases: ['className'] },
  coachName:      { aliases: ['trainerName'] },
  attendedCount:  { aliases: ['attendanceCount', 'reserveCount', 'totalAttendedCount'], keywords: ['출석', '출석횟수'], defaultSort: 'desc' },
  // 예약 출석률(가장 specific) > 출석률(중간) > 참여율(generic). longest-first 매칭으로 정원 대비 출석률은 utilizationRate가 우선 (정원 대비/이용률 substring 더 길지 않으므로 우선순위 분리 필요 — guard에서 정원 substring 우선 확인) //
  attendedReservationRatio: { aliases: ['attendanceRate'], keywords: ['예약 출석률', '출석률', '참여율'], defaultSort: 'desc' },
  weeklyFrequency:{ aliases: [] },
  lastVisit:      { aliases: [], keywords: ['방문', '최근방문'], defaultSort: 'desc' },

  // 통계/비교 도메인 (이탈/잔존/이용률)
  churnRate:       { aliases: [], keywords: ['이탈률', '이탈'], defaultSort: 'desc' },
  retentionRate:   { aliases: [], keywords: ['잔존율', '잔존', '리텐션'], defaultSort: 'desc' },
  utilizationRate: { aliases: [], keywords: ['정원 대비', '정원대비', '이용률', '가동률'], defaultSort: 'desc' },
  reserveCount:   { aliases: ['reservationCount'] },
  // [SORT_INTENT_GUARD] 참여 인원/예약 건수/예약 인원 → totalReservations (CHECK_RESERVE+ATTENDANCE_STATISTICS groupBy lesson/coach/day/time item field)
  // 본질: "참여 인원 많은 수업" 의도인데 LLM이 utilizationRate ASC로 잘못 선택하는 stochastic 잔존 5% 차단용 keyword 추가
  totalReservations: { aliases: [], keywords: ['참여 인원', '예약 건수', '예약 인원', '수강 인원'], defaultSort: 'desc' },
  classTime:      { aliases: [] },

  // 집계 파생 필드
  _count:         { aliases: [] },
  _lastDate:      { aliases: [] },
  _firstDate:     { aliases: [] },

  // 기타
  lockerName:     { aliases: ['locker'] },
  // [SORT_INTENT_GUARD] '가장 오래 다닌 회원' = 재직 기간 → 가입일(createdDate) 오름차순(가입 가장 이른=가장 오래). //
  // 운영 5차 rid150: LLM이 '오래 다닌'을 birthDay(최고령)/viewStartDate(현 회원권 시작)로 stochastic 오매핑 → createdDate asc로 결정 override. //
  // keyword '오래 다'/'오래다'는 '오래 다닌/다니/다녔/다님' 변형 cover. '오래된'(마지막출석/회원권)·'오래 밀린'(연체)·'오래 남'(잔존)과 비충돌. defaultSort asc(direction 미검출 시). //
  createdDate:    { aliases: ['registDate', 'joinDate'], keywords: ['오래 다', '오래다'], defaultSort: 'asc' },
  holdingMembers: { aliases: ['holdingStatus'] },
  refundPrice:    { aliases: ['refundAmount'] },
  isRefund:       { aliases: ['refund'], normalize: true },
  memo:           { aliases: ['note', 'description'] },
  remainCount:    { aliases: [] },
};

// ============================================
// 2. 유틸 함수
// ============================================

// 필드명 → alias 포함 전체 목록 반환 //
// alias로 조회 시에도 canonical + 전체 alias 목록 반환 (양방향 해소)
export function getFieldNames(fieldKey) {
  const entry = FIELD_REGISTRY[fieldKey];

  if (entry) return [fieldKey, ...(entry.aliases || [])];

  // fieldKey가 다른 canonical의 alias인 경우 → canonical + 전체 alias 반환
  for (const [canonical, config] of Object.entries(FIELD_REGISTRY)) {
    if (config.aliases && config.aliases.includes(fieldKey)) {
      return [fieldKey, canonical, ...config.aliases.filter(a => a !== fieldKey)];
    }
  }

  return [fieldKey];
}

// alias → canonical 매핑 (normalize: true인 필드만) //
export function buildAliasMap() {
  const map = {};
  for (const [canonical, config] of Object.entries(FIELD_REGISTRY)) {
    if (!config.normalize) continue;
    for (const alias of config.aliases || []) {
      map[alias] = canonical;
    }
  }
  return map;
}

// availableFields 키 → sampleItem에 실제 존재하는 키로 매핑 //
// 직접 매칭 → dot-path 마지막 segment → canonical의 aliases 탐색 → 역방향(alias→canonical+형제) 탐색 //
export function resolveToItemKey(fieldKey, sampleItem) {
  if (!sampleItem) return null;
  if (fieldKey in sampleItem) return fieldKey;

  // dot-path 평탄화: "sales.name" → "name" (extractArray가 하위 배열 추출 후, 마지막 segment가 sample에 평탄 존재) //
  if (fieldKey.includes('.')) {
    const last = fieldKey.split('.').pop();
    if (last && last in sampleItem) return last;
  }

  // fieldKey가 canonical인 경우: aliases에서 탐색
  const entry = FIELD_REGISTRY[fieldKey];
  if (entry?.aliases) {
    for (const alias of entry.aliases) {
      if (alias in sampleItem) return alias;
    }
  }

  // fieldKey가 alias인 경우: canonical + 형제 aliases에서 탐색
  for (const [canonical, config] of Object.entries(FIELD_REGISTRY)) {
    if (!(config.aliases || []).includes(fieldKey)) continue;
    if (canonical in sampleItem) return canonical;
    for (const alias of config.aliases) {
      if (alias in sampleItem) return alias;
    }
  }

  return null;
}

// compute 설정 조회 //
export function getComputeConfig(field, compute) {
  const entry = FIELD_REGISTRY[field];

  return entry?.computes?.[compute] || null;
}

// ============================================
// 3. Display 라벨 (UI 렌더링용)
// ============================================

// 정적 fallback — 백엔드 availableFields 메타가 없을 때 사용
// Cat6-RAW_VALUE — 영문 enum 값 → 한국어 라벨 매핑 (finalText 값 표출 시 fallback)
const DISPLAY_LABELS = {
  gender:      { M: '남', male: '남', F: '여', female: '여' },
  state:       { Using: '사용중', Empty: '빈락커', Invalid: '사용불가', Expected: '예약중' },
  approval:    { 0: '대기중', 1: '승인', 2: '거절' },
  paymentType: { GENERAL: '일반 결제', SUBSCRIPTION: '구독 결제' },
  // status — 회원권 status (numeric) + 출석 status (영문 enum) 공용 영역
  //   회원권 numeric: 1/2/3/4 (활성/만료/홀딩/환불)
  //   출석 enum: attended/absent/late/cancelled/notReserved (백엔드 availableFields enums 미정의 시 fallback)
  status:      {
    1: '활성', 2: '만료', 3: '홀딩', 4: '환불',
    attended: '출석', absent: '결석', late: '지각', cancelled: '취소', notReserved: '미예약',
  },
  totalMax:    { '-1': '무제한' },
  // dayOfWeek — 요일 영문 enum → 한국어 (SYN-CHECK_RESERVE+ATTENDANCE_STATISTICS류 통계 응답)
  dayOfWeek:   { MON: '월', TUE: '화', WED: '수', THU: '목', FRI: '금', SAT: '토', SUN: '일' },
};

// 백엔드 availableFields의 값이 enum 메타 object인지 판별 //
// (그룹 nested object와 구분 — initBuffer의 중첩 구조 해소 로직과 호환) //
export function isFieldMetaObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v) && ('label' in v || 'enums' in v || 'dimension' in v);
}

// ─────────────────────────────────────────────────────────────────────────────
// Field label sanitize — strip developer annotations leaking into user-facing
// column headers / LLM context (DEV_COMMENT_LEAK cluster ~26 cases).
// ─────────────────────────────────────────────────────────────────────────────
// 대상 패턴 (괄호 안에 disambiguation/note만 있는 경우만 strip):
//   "회원권 잔여 이용 횟수 (NOT 출석 횟수 — 출석 횟수는 SCHEDULE의 attendedCount)"
//   "잔여 횟수 (NOT 출석)" / "X (NOTE: ...)" / "Y (참고: ...)" / "Z (cf. ...)"
//   "P — Q is internal" (em-dash 뒤 영문 sentence-level explanation)
// 보존: "(%)", "(원)", "(명)", "(회)", "(KRW)", "(점)" 같은 단위/dimension 토큰.
// ─────────────────────────────────────────────────────────────────────────────

// 한 토큰이 unit/dimension 표기인지 판정 — 짧고 simple keyword 만 유지 //
const UNIT_TOKEN_RE = /^[\s]*(?:[%a-zA-Z가-힣]{1,6}(?:\/[%a-zA-Z가-힣]{1,6})?)[\s]*$/;
const DEV_ANNOTATION_KEYWORDS = /(?:\bNOT\b|\bNOTE\b\s*:?|note\s*:|cf\.|i\.e\.|e\.g\.|참고\s*[:：]|참조\s*[:：]|주의\s*[:：]|주석)/;

// 라벨 sanitize — dev annotation strip, unit/dimension 보존 //
// idempotent: 두 번 호출해도 동일 결과 //
export function sanitizeFieldLabel(rawLabel) {
  if (rawLabel == null) return rawLabel;
  if (typeof rawLabel !== 'string') return rawLabel;
  let s = rawLabel;

  // 1) 괄호 그룹 처리 — dev annotation 키워드 포함 또는 em-dash 가 포함된 긴 explanation 만 strip
  s = s.replace(/\s*\(([^()]*)\)\s*/g, (_m, inner) => {
    // unit token (짧음): 그대로 보존
    if (UNIT_TOKEN_RE.test(inner)) return `(${inner.trim()})`;
    // dev annotation keyword 포함 → strip
    if (DEV_ANNOTATION_KEYWORDS.test(inner)) return ' ';
    // em-dash 포함된 sentence-level explanation (괄호 내부 — 도 dev annotation 신호)
    if (inner.includes('—') && inner.length > 12) return ' ';
    // 그 외 → 보존 (도메인 부연 가능성)
    return `(${inner.trim()})`;
  });

  // 2) em-dash 뒤 영문/한글 sentence-level explanation strip — dev keyword가 명시된 경우만
  //    "X — NOT Y" / "X — NOTE: ..." 만 cover. "X — 평균" / "X — 합계" 짧은 부연은 보존.
  s = s.replace(/\s+—\s+[^—]*$/g, (m) => DEV_ANNOTATION_KEYWORDS.test(m) ? '' : m);

  // 3) 다중 공백 정리
  s = s.replace(/\s{2,}/g, ' ').trim();
  return s;
}

// availableFields 정규화 — 모든 값을 { label, enums? } 형태로 통일 //
// string("이름") → { label: "이름" } / { label, enums } → 그대로 유지 //
// 모든 label에 sanitizeFieldLabel 적용 — DEV_COMMENT_LEAK 차단 //
export function normalizeAvailableFields(af) {
  if (!af || typeof af !== 'object') return {};
  const result = {};
  for (const [k, v] of Object.entries(af)) {
    if (typeof v === 'string') {
      result[k] = { label: sanitizeFieldLabel(v) };
    } else if (isFieldMetaObject(v)) {
      const cleaned = { ...v };
      if (typeof cleaned.label === 'string') cleaned.label = sanitizeFieldLabel(cleaned.label);
      result[k] = cleaned;
    } else {
      // 그룹 nested object 등 — 그대로 보존 (기존 구조 유지)
      result[k] = v;
    }
  }
  return result;
}

// 필드 메타에서 라벨 추출 (string·object 모두 처리) //
// 방어층: getFieldLabel 시점에도 sanitize — initBuffer 우회한 raw 메타 차단 //
export function getFieldLabel(fieldMeta) {
  if (fieldMeta == null) return null;
  if (typeof fieldMeta === 'string') return sanitizeFieldLabel(fieldMeta);
  if (isFieldMetaObject(fieldMeta)) {
    const label = fieldMeta.label ?? null;
    return typeof label === 'string' ? sanitizeFieldLabel(label) : label;
  }
  return null;
}

// enum 맵에서 키를 대소문자 무시로 조회 (exact 실패 후 fallback 전용) //
function lookupEnumIgnoreCase(map, valStr) {
  const lower = valStr.toLowerCase();
  for (const k of Object.keys(map)) {
    if (k.toLowerCase() === lower) return map[k];
  }
  return undefined;
}

// 값 → 디스플레이 라벨 변환 //
// fieldMeta(백엔드 availableFields[field]) 우선 → 정적 DISPLAY_LABELS fallback //
// [A-3 운영 rid170] enum 값 lookup은 exact 우선 → 대소문자 무시 fallback. 백엔드가 비표준 casing(예: gender 'Female'/'Male')을
//   섞어 응답해도 raw 누출 차단. exact-first라 기존 enum(대문자 Using/MON/attended 등) 동작 보존, 추가만 하므로 안전(충돌 키 없음).
export function getDisplayLabel(field, value, fieldMeta) {
  const valStr = String(value);
  // 1) 백엔드 enum 메타 우선 (exact → 대소문자 무시 fallback)
  if (isFieldMetaObject(fieldMeta) && fieldMeta.enums) {
    const enumLabel = fieldMeta.enums[valStr];
    if (enumLabel !== undefined) return enumLabel;
    const ciHit = lookupEnumIgnoreCase(fieldMeta.enums, valStr);
    if (ciHit !== undefined) return ciHit;
  }
  // 2) 정적 DISPLAY_LABELS (gender/state/approval/paymentType) (exact → 대소문자 무시 fallback)
  const staticMap = DISPLAY_LABELS[field];
  if (staticMap) {
    if (staticMap[value] !== undefined) return staticMap[value];
    const ciHit = lookupEnumIgnoreCase(staticMap, valStr);
    if (ciHit !== undefined) return ciHit;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// System placeholder value sanitize — backend가 미등록/미가입 회원에 일괄로
// 채워넣는 placeholder string이 finalText 표 cell에 그대로 노출되는 leak 차단.
// 예: nickName 필드는 미가입 회원(userId="None-…")에 "미가입" string으로 응답되어
//     "닉네임 | 미가입" 형태로 사용자 노출 → 시스템 내부 표현 leak.
// 본 helper는 per-field whitelist (도메인 한정 — 일반 row data를 false-positive로
// 변환하지 않도록). 신규 패턴 발견 시 항목 추가.
// ─────────────────────────────────────────────────────────────────────────────
const SYSTEM_PLACEHOLDER_VALUES = {
  nickName: new Set(['미가입', '미설정', '미등록']),
};

// field별 placeholder system value 여부 판정 //
// true 반환 시 호출자가 빈값(또는 "-") 등 user-facing 표기로 치환 권장 //
export function isSystemPlaceholderValue(field, value) {
  if (typeof value !== 'string') return false;
  const set = SYSTEM_PLACEHOLDER_VALUES[field];
  return !!(set && set.has(value));
}
